export * from "./types.js";
export * from "./copy.js";
export * from "./benchmark-types.js";
export * from "./hospital-premiums-types.js";
export { calculateAu08 } from "./calculate.js";
export {
  deriveAu08Income,
  buildAu08InputFromComponents,
} from "./derive-income.js";
export type { Au08IncomeComponents } from "./derive-income.js";
export { fy2025_26 } from "./fy2025-26.js";
// FY2026-27 — income-tests wiring shipped, MLS thresholds PLACEHOLDER (data
// gate). Intentionally NOT added to AU08_RULESETS/AU08_CURRENT_FY below.
export {
  fy2026_27,
  FY2026_27 as AU08_FY2026_27,
  FY2026_27_THRESHOLDS_ARE_PLACEHOLDER as AU08_FY2026_27_PLACEHOLDER,
  deriveFy2026_27MlsIncome,
} from "./fy2026-27.js";
export type { Au08Fy2026_27ComponentInput } from "./fy2026-27.js";
export {
  benchmarks2026Q1,
  APRA_2026_Q1_TOTAL_HOSPITAL_INSURED,
  ABS_ERP_2025_Q3_TOTAL,
} from "./benchmarks-2026-q1.js";
export { hospitalPremiums2026_06 } from "./hospital-premiums-2026-06.js";

import type { FinancialYear } from "../types.js";
import type { Au08RuleSet } from "./types.js";
import { fy2025_26 } from "./fy2025-26.js";
import type { Au08BenchmarkDataset } from "./benchmark-types.js";
import { benchmarks2026Q1 } from "./benchmarks-2026-q1.js";
import type { Au08HospitalPremiumDataset } from "./hospital-premiums-types.js";
import { hospitalPremiums2026_06 } from "./hospital-premiums-2026-06.js";

/** Registry of AU-08 rulesets by financial year. Historical years stay
 * registered so reclaim/amendment flows resolve against the right rules. */
export const AU08_RULESETS: Record<string, Au08RuleSet> = {
  "2025-26": fy2025_26,
};

/** Resolve the AU-08 ruleset for a financial year, or throw if unknown. */
export function au08RulesFor(fy: FinancialYear): Au08RuleSet {
  const rules = AU08_RULESETS[fy];
  if (!rules) {
    throw new Error(`no AU-08 ruleset registered for FY ${fy}`);
  }
  return rules;
}

/** Most recent AU-08 ruleset — the default for live "this year" calcs. */
export const AU08_CURRENT_FY: FinancialYear = "2025-26";

/** Registry of AU-08 "How you compare" benchmark datasets, keyed by the period
 * they cover. Historical periods stay registered (immutability rule — a refresh
 * is a new dataset, never a mutation of a shipped one). */
export const AU08_BENCHMARKS: Record<string, Au08BenchmarkDataset> = {
  "apra-2026-q1": benchmarks2026Q1,
};

/** Most recent AU-08 benchmark dataset — the default for the live result-page
 * comparison strip. */
export const AU08_CURRENT_BENCHMARK: Au08BenchmarkDataset = benchmarks2026Q1;

/** Resolve a benchmark dataset by its period id, or throw if unknown. */
export function au08BenchmarkFor(id: string): Au08BenchmarkDataset {
  const dataset = AU08_BENCHMARKS[id];
  if (!dataset) {
    throw new Error(`no AU-08 benchmark dataset registered for "${id}"`);
  }
  return dataset;
}

/** Registry of AU-08 hospital-premium reference datasets, keyed by the
 * privatehealth.gov.au release they were built from. Historical releases stay
 * registered (immutability rule — a refresh is a new dataset, never a mutation
 * of a shipped one). */
export const AU08_HOSPITAL_PREMIUMS: Record<string, Au08HospitalPremiumDataset> =
  {
    "phis-2026-06": hospitalPremiums2026_06,
  };

/** Most recent hospital-premium dataset — the default for the live PDF report's
 * hospital-comparison page. */
export const AU08_CURRENT_HOSPITAL_PREMIUMS: Au08HospitalPremiumDataset =
  hospitalPremiums2026_06;

/** Resolve a hospital-premium dataset by its release id, or throw if unknown. */
export function au08HospitalPremiumsFor(
  id: string,
): Au08HospitalPremiumDataset {
  const dataset = AU08_HOSPITAL_PREMIUMS[id];
  if (!dataset) {
    throw new Error(`no AU-08 hospital-premium dataset registered for "${id}"`);
  }
  return dataset;
}
